import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { buildQrMatrix } from '../utils/qrcode';

/**
 * Renderiza um QR Code (gerado 100% no front, sem dependências) em um <canvas>.
 * Expõe, via ref, `toDataURL()` e `download(filename)` para baixar como PNG.
 *
 * Props:
 * - value: texto/URL codificado no QR
 * - size: tamanho renderizado em CSS px (o canvas usa devicePixelRatio)
 * - ecc: nível de correção ('L' | 'M' | 'Q' | 'H')
 * - margin: quiet zone em módulos (padrão 4)
 * - dark / light: cores dos módulos
 * - exportScale: px por módulo ao exportar o PNG (nitidez do download)
 */
const QrCodeCanvas = forwardRef(function QrCodeCanvas(
  {
    value,
    size = 208,
    ecc = 'M',
    margin = 4,
    dark = '#0b0f1d',
    light = '#ffffff',
    exportScale = 16,
    ...rest
  },
  ref
) {
  const canvasRef = useRef(null);

  const matrix = useMemo(() => {
    if (!value) return null;
    try {
      return buildQrMatrix(value, ecc);
    } catch (err) {
      // Conteúdo longo demais / inválido — não quebra a tela.
      console.error('Falha ao gerar QR Code:', err);
      return null;
    }
  }, [value, ecc]);

  // Desenha uma matriz num contexto 2D, ocupando `pixels` x `pixels`.
  const paint = (ctx, pixels) => {
    if (!matrix) return;
    const total = matrix.size + margin * 2;
    const cell = pixels / total;
    ctx.fillStyle = light;
    ctx.fillRect(0, 0, pixels, pixels);
    ctx.fillStyle = dark;
    for (let y = 0; y < matrix.size; y++) {
      for (let x = 0; x < matrix.size; x++) {
        if (matrix.modules[y][x]) {
          // +0.5 evita hairlines entre módulos no arredondamento.
          ctx.fillRect(
            Math.round((x + margin) * cell),
            Math.round((y + margin) * cell),
            Math.ceil(cell),
            Math.ceil(cell)
          );
        }
      }
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !matrix) return;
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const pixels = Math.round(size * dpr);
    canvas.width = pixels;
    canvas.height = pixels;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    const ctx = canvas.getContext('2d');
    if (ctx) paint(ctx, pixels);
  }); // redesenha sempre que props relevantes mudam (matrix/size/cores)

  // Gera um PNG em alta resolução, independente do tamanho exibido.
  const toDataURL = () => {
    if (!matrix) return null;
    const total = matrix.size + margin * 2;
    const pixels = total * exportScale;
    const off = document.createElement('canvas');
    off.width = pixels;
    off.height = pixels;
    const ctx = off.getContext('2d');
    if (!ctx) return null;
    paint(ctx, pixels);
    return off.toDataURL('image/png');
  };

  useImperativeHandle(ref, () => ({
    toDataURL,
    download: (filename = 'qrcode.png') => {
      const url = toDataURL();
      if (!url) return false;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return true;
    },
  }));

  if (!matrix) {
    return (
      <div
        style={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 12,
          background: light,
          color: '#94a3b8',
          fontSize: 13,
          textAlign: 'center',
          padding: 12,
          boxSizing: 'border-box',
        }}
        {...rest}
      >
        QR indisponível
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label={`QR Code para ${value}`}
      style={{ borderRadius: 12, display: 'block' }}
      {...rest}
    />
  );
});

export default QrCodeCanvas;
